import { defineNuxtRouteMiddleware, navigateTo } from '#app/composables/router';
import { CONTENT_COLLECTION_ROUTE_NAME, CONTENT_ROUTE_NAME, useContentPages, WORKSPACE_HOME_ROUTE_NAME } from '#layers/spaces/public';

/**
 * Resolves where a workspace "landing" route (the workspace root and the content root) should
 * settle, and performs a single atomic redirect keyed off the *target* route.
 *
 * This must run after `switch-workspace`, which publishes the target workspace's prepared schema. Resolving
 * the destination here — rather than via `router.replace` inside page `setup()` — avoids the
 * class of bug where a partial named redirect (`{ name }` without an explicit `workspace`) runs
 * mid-navigation and inherits the *previous* workspace from a not-yet-settled `currentRoute`,
 * cancelling a workspace switch and dropping the user on a 404.
 */
export default defineNuxtRouteMiddleware((to) => {
	if (to.name !== WORKSPACE_HOME_ROUTE_NAME && to.name !== CONTENT_ROUTE_NAME) {
		return;
	}

	const workspace = to.params.workspace;

	if (typeof workspace !== 'string') {
		return;
	}

	const { visibleContentPages } = useContentPages();
	const firstCollection = visibleContentPages.value[0]?.route;

	if (firstCollection) {
		return navigateTo(
			// Preserve query + hash so the settings overlay (`?settings=…`) and in-panel anchors
			// survive the landing redirect (e.g. a `/~/account/access` external deep link).
			{ name: CONTENT_COLLECTION_ROUTE_NAME, params: { workspace, collection: firstCollection }, query: to.query, hash: to.hash },
			{ replace: true },
		);
	}

	// Empty workspace: send the workspace root to the content root so it renders the empty state at
	// a stable URL. When already at the content root, fall through and let it render that state.
	if (to.name === WORKSPACE_HOME_ROUTE_NAME) {
		return navigateTo(
			{ name: CONTENT_ROUTE_NAME, params: { workspace }, query: to.query, hash: to.hash },
			{ replace: true },
		);
	}
});
